import { FormEvent, useEffect, useState, type CSSProperties } from "react";
import { Badge, Button, IconButton, Input, Panel, StatusDot, Tag } from "../design-system/components";
import { Icon, type IconName } from "../design-system/Icon";

const FILE_COLORS: Record<string, string> = {
  pdf: "#e5604d",
  csv: "#4dbf7a",
  md: "#4cc6d6",
  xls: "#4dbf7a",
  json: "#e8b046",
};

type FileStatus = "indexed" | "pending" | "error";

const HERO_FILES: Array<{ name: string; type: string; size: string; status: FileStatus }> = [
  { name: "2024-Q3-statements.pdf", type: "pdf", size: "2.3 MB", status: "indexed" },
  { name: "invoice-8841.pdf", type: "pdf", size: "412 KB", status: "indexed" },
  { name: "vendors.csv", type: "csv", size: "88 KB", status: "indexed" },
  { name: "nda-2024.pdf", type: "pdf", size: "210 KB", status: "pending" },
  { name: "scan-batch-7.pdf", type: "pdf", size: "14 MB", status: "error" },
];

const FEATURES: Array<{ icon: IconName; title: string; body: string }> = [
  {
    icon: "hard-drive",
    title: "Scoped per user/agent",
    body: "One isolated drive per end user or agent. Path-scoped, mode-scoped, region-pinned. Agents only ever see what you mount.",
  },
  {
    icon: "scan-line",
    title: "OCR on ingest",
    body: "Every PDF, scan, and image is OCR'd and chunked the moment it lands. No pipeline to wire up.",
  },
  {
    icon: "search",
    title: "Agentic search",
    body: "Full POSIX-style file navigation and search tools over the drive, exposed as MCP tools. Sub-100ms.",
  },
  {
    icon: "box",
    title: "Sandbox mount",
    body: "Drives mount inside a per-session sandbox as a POSIX-compliant local filesystem. The agent never leaves it; you keep the keys.",
  },
  {
    icon: "git-branch",
    title: "Branch, diff, merge",
    body: "Every write is an immutable revision. Diff what an agent changed, then merge or roll the drive back.",
  },
  {
    icon: "key",
    title: "Scoped keys",
    body: "Issue read-only or read-write keys per drive, per scope, per expiry. Revoke in one call.",
  },
];

const STEPS: Array<{ n: string; icon: IconName; title: string; body: string; code: string }> = [
  {
    n: "01",
    icon: "hard-drive",
    title: "Provision a drive",
    body: "Create one private drive per user or agent. Region-pinned, isolated, scoped.",
    code: "aidrive.drives.create(userId)",
  },
  {
    n: "02",
    icon: "layers",
    title: "Files ingest themselves",
    body: "Drop in PDFs, Office files, and scans. OCR, chunking, and embeddings happen on landing.",
    code: 'aidrive.put(userId, "msa.pdf")',
  },
  {
    n: "03",
    icon: "box",
    title: "Agents mount and search",
    body: "Mount the drive in a sandbox; the agent reads, searches, and writes with reviewable revisions.",
    code: "agent.callTool(\"drive.search\")",
  },
];

const eyebrow: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--text-2xs)",
  fontWeight: 600,
  letterSpacing: "var(--tracking-wider)",
  textTransform: "uppercase",
  color: "var(--text-faint)",
};

type WaitlistState = "idle" | "submitting" | "success" | "error";

function statusCell(status: FileStatus) {
  if (status === "indexed") {
    return <span style={{ color: "var(--status-online)" }}>indexed</span>;
  }
  if (status === "pending") {
    return <Badge tone="agent">pending</Badge>;
  }
  return <Badge tone="error">error</Badge>;
}

function LogoMark({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="62" height="62" rx="8" fill="#222529" stroke="#393e47" strokeWidth="2" />
      <path d="M22 18 H17 V46 H22" stroke="#76767f" strokeWidth="2.5" strokeLinecap="square" fill="none" />
      <path d="M42 18 H47 V46 H42" stroke="#76767f" strokeWidth="2.5" strokeLinecap="square" fill="none" />
      <rect x="26" y="22" width="12" height="3.4" rx="1.2" fill="#e8b046" />
      <rect x="26" y="30.3" width="12" height="3.4" rx="1.2" fill="#e8b046" opacity="0.7" />
      <rect x="26" y="38.6" width="7.5" height="3.4" rx="1.2" fill="#4dbf7a" />
    </svg>
  );
}

function Nav({ theme, onToggleTheme }: { theme: string; onToggleTheme: () => void }) {
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        gap: 24,
        padding: "0 32px",
        height: 60,
        background: "var(--surface-nav)",
        backdropFilter: "blur(10px)",
        borderBottom: "1px solid var(--border-strong)",
      }}
    >
      <a href="#top" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
        <LogoMark />
        <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: "var(--text-md)", color: "var(--text-strong)", letterSpacing: "0" }}>
          AI<span style={{ color: "var(--accent)" }}>/</span>Drive
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--text-faint)" }}>::fs</span>
      </a>
      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
        <a
          href="#waitlist"
          style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)", color: "var(--text-muted)", textDecoration: "none" }}
        >
          Join waitlist
        </a>
        <IconButton label="Toggle theme" onClick={onToggleTheme}>
          <Icon name={theme === "light" ? "moon" : "sun"} />
        </IconButton>
      </div>
    </header>
  );
}

function WaitlistForm({ compact = false, id = "waitlist" }: { compact?: boolean; id?: string }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<WaitlistState>("idle");
  const [message, setMessage] = useState<string | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = email.trim();
    if (!value) {
      setState("error");
      setMessage("Enter your email to join the waitlist.");
      return;
    }

    setState("submitting");
    setMessage(null);
    try {
      const response = await fetch("/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value }),
      });
      if (!response.ok) {
        throw new Error(`Waitlist request failed with ${response.status}`);
      }
      setState("success");
      setEmail("");
      setMessage("You're on the waitlist. We'll email you when preview access is ready.");
    } catch {
      setState("error");
      setMessage("We couldn't join the waitlist. Try again in a moment.");
    }
  };

  return (
    <form id={id} className="lp-cta-row" onSubmit={submit} style={{ display: "flex", gap: 10, maxWidth: compact ? 420 : 500 }}>
      <Input
        prefix="@"
        type="email"
        name="email"
        autoComplete="email"
        placeholder="you@company.com"
        value={email}
        onChange={(event) => setEmail(event.currentTarget.value)}
        aria-label="Email address"
        style={{ flex: 1 }}
      />
      <Button variant="primary" type="submit" loading={state === "submitting"} iconRight={<Icon name="arrow-right" size={14} />}>
        Join waitlist
      </Button>
      {message && (
        <p
          role={state === "error" ? "alert" : "status"}
          style={{
            flexBasis: "100%",
            margin: "2px 0 0",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
            color: state === "error" ? "var(--status-error)" : "var(--status-online)",
          }}
        >
          {message}
        </p>
      )}
    </form>
  );
}

function Hero() {
  return (
    <section id="top" style={{ position: "relative", padding: "72px 32px 64px", overflow: "hidden", borderBottom: "1px solid var(--border-strong)" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(720px 360px at 22% -10%, rgba(232,176,70,0.10), transparent 70%)", pointerEvents: "none" }} />
      <div className="lp-hero-grid" style={{ position: "relative", maxWidth: 1180, margin: "0 auto", display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.05fr)", gap: 48, alignItems: "center" }}>
        <div>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(34px, 4.4vw, 56px)", lineHeight: 1.05, letterSpacing: "var(--tracking-tighter)", color: "var(--text-strong)", margin: "0 0 20px" }}>
            <span style={{ color: "var(--accent)" }}>AI-native</span> filesystem for agents
          </h1>
          <p style={{ fontFamily: "var(--font-prose)", fontSize: "var(--text-lg)", lineHeight: "var(--leading-relaxed)", color: "var(--text-muted)", maxWidth: 520, margin: "0 0 28px" }}>
            A private, per-user cloud drive with ingestion, OCR, and agent search built in. Join the waitlist for early preview access.
          </p>
          <WaitlistForm />
          <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 28, fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-faint)" }}>
            <span>SOC 2 Type II</span><span>·</span><span>per-user isolation</span><span>·</span><span>99.99% uptime</span><span>·</span><span>multiple regions</span>
          </div>
        </div>

        <div style={{ minWidth: 0 }}>
          <Panel
            title="agent-fs"
            path="~/drives/acme_user_42/invoices"
            glow
            bodyStyle={{ padding: 0 }}
            actions={<StatusDot tone="online" pulse size={7} label="mounted" />}
            footer={
              <>
                <span>{HERO_FILES.length} objects</span><span>·</span><span>2.3 GB</span><span>·</span>
                <span style={{ color: "var(--status-online)" }}>indexed {HERO_FILES.filter((file) => file.status === "indexed").length}/{HERO_FILES.length}</span>
                <span style={{ marginLeft: "auto", color: "var(--text-faint)" }}>sbx-7f3a</span>
              </>
            }
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-mono)", fontSize: "var(--text-sm)" }}>
              <thead>
                <tr style={{ textAlign: "left", color: "var(--text-faint)", fontSize: "var(--text-2xs)", textTransform: "uppercase", letterSpacing: "var(--tracking-wide)" }}>
                  <th style={{ padding: "10px 16px", fontWeight: 500 }}>Name</th>
                  <th style={{ padding: "10px 8px", fontWeight: 500 }}>Type</th>
                  <th style={{ padding: "10px 8px", fontWeight: 500 }}>Size</th>
                  <th style={{ padding: "10px 16px", fontWeight: 500 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {HERO_FILES.map((file) => (
                  <tr key={file.name} style={{ borderTop: "1px solid var(--border-faint)" }}>
                    <td style={{ padding: "11px 16px", color: "var(--text-body)" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
                        <span style={{ color: FILE_COLORS[file.type] || "var(--text-faint)", display: "inline-flex" }}><Icon name="file-text" size={14} /></span>
                        {file.name}
                      </span>
                    </td>
                    <td style={{ padding: "11px 8px" }}><Tag color={FILE_COLORS[file.type]}>{file.type}</Tag></td>
                    <td style={{ padding: "11px 8px", color: "var(--text-muted)" }}>{file.size}</td>
                    <td style={{ padding: "11px 16px" }}>{statusCell(file.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </div>
      </div>
    </section>
  );
}

function Features() {
  return (
    <section style={{ padding: "72px 32px", borderBottom: "1px solid var(--border-strong)" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <span style={eyebrow}>// primitives</span>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "var(--text-3xl)", letterSpacing: "var(--tracking-tight)", color: "var(--text-strong)", margin: "10px 0 0" }}>
            Everything an agent needs from a filesystem
          </h2>
        </div>
        <div className="lp-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {FEATURES.map((feature) => (
            <div key={feature.title} style={{ padding: 24, background: "var(--surface-panel)", border: "1px solid var(--border-strong)", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-sm)" }}>
              <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 40, height: 40, borderRadius: "var(--radius-md)", background: "var(--amber-glow)", border: "1px solid rgba(232,176,70,0.3)", marginBottom: 16, color: "var(--accent)" }}>
                <Icon name={feature.icon} size={18} />
              </div>
              <h3 style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-strong)", margin: "0 0 8px" }}>{feature.title}</h3>
              <p style={{ fontFamily: "var(--font-prose)", fontSize: "var(--text-sm)", lineHeight: "var(--leading-normal)", color: "var(--text-muted)", margin: 0 }}>{feature.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function How() {
  return (
    <section style={{ padding: "72px 32px", borderBottom: "1px solid var(--border-strong)", background: "var(--surface-sunken)" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <span style={eyebrow}>// how it works</span>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "var(--text-3xl)", letterSpacing: "var(--tracking-tight)", color: "var(--text-strong)", margin: "10px 0 0" }}>
            From zero to mounted
          </h2>
        </div>
        <div className="lp-grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {STEPS.map((step) => (
            <div key={step.n} style={{ padding: 24, background: "var(--surface-panel)", border: "1px solid var(--border-strong)", borderRadius: "var(--radius-lg)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                <span style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-2xs)", fontWeight: 700, color: "var(--accent)", letterSpacing: "var(--tracking-wider)" }}>{step.n}</span>
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: "var(--radius-md)", background: "var(--amber-glow)", border: "1px solid rgba(232,176,70,0.3)", color: "var(--accent)" }}>
                  <Icon name={step.icon} size={16} />
                </span>
              </div>
              <h3 style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-md)", fontWeight: 600, color: "var(--text-strong)", margin: "0 0 8px" }}>{step.title}</h3>
              <p style={{ fontFamily: "var(--font-prose)", fontSize: "var(--text-sm)", lineHeight: "var(--leading-normal)", color: "var(--text-muted)", margin: "0 0 14px" }}>{step.body}</p>
              <code style={{ display: "block", fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-link)", background: "var(--black)", border: "1px solid var(--border-strong)", borderRadius: "var(--radius-sm)", padding: "8px 10px", overflowX: "auto" }}>{step.code}</code>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer>
      <section style={{ padding: "84px 32px", textAlign: "center", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(560px 260px at 50% 120%, rgba(232,176,70,0.10), transparent 70%)" }} />
        <div style={{ position: "relative", maxWidth: 560, margin: "0 auto" }}>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "var(--text-4xl)", letterSpacing: "var(--tracking-tight)", color: "var(--text-strong)", margin: "0 0 14px" }}>
            Join the waitlist
          </h2>
          <p style={{ fontFamily: "var(--font-prose)", fontSize: "var(--text-md)", color: "var(--text-muted)", margin: "0 0 26px" }}>
            Get notified when AI Drive FS preview access opens.
          </p>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <WaitlistForm compact id="waitlist-footer" />
          </div>
        </div>
      </section>

      <div style={{ padding: "28px 32px", borderTop: "1px solid var(--border-strong)", background: "var(--surface-sunken)" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <LogoMark size={24} />
            <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "var(--text-strong)" }}>AI<span style={{ color: "var(--accent)" }}>/</span>Drive</span>
          </div>
          <p style={{ fontFamily: "var(--font-mono)", fontSize: "var(--text-xs)", color: "var(--text-faint)", lineHeight: 1.6, margin: 0 }}>
            Agent-first storage. © 2026 AI Drive, Inc.
          </p>
        </div>
      </div>
    </footer>
  );
}

export function WaitlistLanding() {
  const [theme, setTheme] = useState<string>(() => localStorage.getItem("aidrive-theme") === "light" ? "light" : "dark");

  useEffect(() => {
    document.documentElement.classList.toggle("theme-light", theme === "light");
    localStorage.setItem("aidrive-theme", theme);
  }, [theme]);

  return (
    <div style={{ background: "var(--surface-app)", color: "var(--text-body)", minHeight: "100vh" }}>
      <Nav theme={theme} onToggleTheme={() => setTheme((value) => (value === "light" ? "dark" : "light"))} />
      <Hero />
      <Features />
      <How />
      <Footer />
    </div>
  );
}
